import { ColDef, GridApi, GridReadyEvent, IDatasource } from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import { forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState } from "react";
import { Box } from "@chakra-ui/react";
import { PipelineImport, PipelineImportRow, QueryFilter } from "../../../api/types";
import { fetchRows } from "../../../api/useGetPipelineImportRows";
import classes from "../../../utils/classes";
import { typedSxMap } from "../../../utils/typedSxMap";
import "./../style/TableStyle.scss";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-alpine.css";
import { CELL_COLOR_MAP, DefaultCellRenderer, StatusCellRenderer } from "./cellRenderer";

type ReviewDataTableProps = {
  pipelineImport?: PipelineImportWithRefresh;
  onSelectRows: (rows: PipelineImportRow[] | null) => void;
  onUpdateRow: (row: PipelineImportRow | null) => void;
  importId?: string;
  filter: QueryFilter;
  theme: any;
  updatedRow: PipelineImportRow | null;
  isVisible: boolean;
  importStatus?: string;
  onColumnLabelsChange?: (labels: { key: string; value: string }[]) => void;
};
export type ReviewDataTableRef = {
  api: GridApi;
  navigateToNextCell: (params: any) => void;
};

const styles = typedSxMap({
  wrapper: {
    flex: "1 1",
    height: "100%",
    overflow: "hidden",
  },
});

const PAGINATION_BLOCK_SIZE = 100;

const initialColumns: ColDef[] = [
  {
    field: "tableIndex",
    headerName: "",
    width: 50,
    suppressMovable: true,
    sortable: false,
    cellStyle: {
      justifyContent: "center",
      display: "flex",
      alignItems: "center",
      padding: "0 8px",
    },
    cellRenderer: undefined,
  },
];

type PipelineImportWithRefresh = PipelineImport & { forceRefresh?: boolean };

const ReviewDataTable = forwardRef<ReviewDataTableRef, ReviewDataTableProps>(
  (
    {
      pipelineImport,
      onSelectRows,
      importId,
      filter,
      theme,
      updatedRow,
      isVisible,
      onUpdateRow,
      importStatus,
      onColumnLabelsChange,
    }: ReviewDataTableProps,
    ref
  ) => {
    const gridRef = useRef<GridApi | null>(null);
    const isFirstTime = useRef<boolean>(true);
    const previousPipelineImport = useRef<string | null>(null);
    const paginatedDataRef: any = useRef();
    const filterRef: any = useRef(filter);
    const [columns, setColumns] = useState<ColDef[]>([]);
    const [columnDefs, setColumnDefs] = useState<ColDef[]>([]);
    const [newRows, setNewRows] = useState<any>(null);
    const isFilterChanging = useRef(false);

    useImperativeHandle(
      ref,
      () => ({
        api: gridRef.current!,
        navigateToNextCell,
      }),
      [gridRef.current]
    );

    useEffect(() => {
      filterRef.current = filter;
      paginatedDataRef.current = null;
      isFilterChanging.current = true;

      gridRef.current && setDataSource();

      setTimeout(() => {
        isFilterChanging.current = false;
      }, 100);
    }, [filter]);

    useEffect(() => {
      if (updatedRow) {
        const { id: rowNodeId, ...rowData } = updatedRow;
        const rowNode = gridRef.current?.getRowNode(rowNodeId.toString());
        rowNode?.updateData({ ...rowData, ...rowData.destination_values, tableIndex: rowNodeId + 1 });
        onUpdateRow(updatedRow);
      }
    }, [updatedRow]);

    useEffect(() => {
      if (pipelineImport?.id) {
        if (pipelineImport.forceRefresh) {
          paginatedDataRef.current = null;
          gridRef.current && setDataSource();
        }
        const schemaColumns: ColDef[] = pipelineImport.dst_schema.map((col) => ({
          field: col.key,
          headerName: col.name,
          resizable: true,
          sortable: false,
          cellRenderer: DefaultCellRenderer,
          cellStyle: (params: any) => {
            if (params.data?.errors?.[col.key]) {
              const severity = params.data.errors[col.key][0]?.severity as keyof typeof CELL_COLOR_MAP;
              return {
                backgroundColor: CELL_COLOR_MAP[severity] || "transparent",
              };
            }
            return { backgroundColor: "transparent" };
          },
        }));

        const newColumns = [...initialColumns, ...schemaColumns];
        setColumns(newColumns);

        const [, ...dataColumns] = newColumns;
        onColumnLabelsChange?.(
          dataColumns.map((col: ColDef) => ({
            key: col.field || "",
            value: col.headerName || "",
          }))
        );
      }
    }, [pipelineImport]);

    useEffect(() => {
      const shouldShowStatus = importStatus === "importing" || importStatus === "complete";
      const statusValueColumn: ColDef = {
        field: "status.value",
        headerName: "Status",
        initialWidth: 150,
        cellRenderer: StatusCellRenderer,
        // tooltipField: "status.value",
        headerClass: "ag-header-italic",
        valueGetter: (params) => {
          const status = params.data?.status?.value;
          switch (status) {
            case "accepted":
              return "Accepted";
            case "failed":
              return "Rejected";
            case "created":
              return "Pending";
          }
          return status;
        },
        cellStyle: {
          fontStyle: "italic",
          color: "var(--color-text-soft-disabled)",
          backgroundColor: "rgba(0, 0, 0, 0.03)",
        },
      };

      const statusMessageColumn: ColDef = {
        field: "status.message",
        headerName: "Message",
        initialWidth: 300,
        maxWidth: 700,
        cellRenderer: DefaultCellRenderer,
        // tooltipField: "status.message",
        headerClass: "ag-header-italic",
        valueGetter: (params) => params.data?.status?.message,
        cellStyle: {
          fontStyle: "italic",
          color: "var(--color-text-soft-disabled)",
          backgroundColor: "rgba(0, 0, 0, 0.03)",
        },
      };

      const columnsWithMaxWidth: ColDef[] = columns.map((col) => ({
        ...col,
        initialWidth: col.field === "tableIndex" ? undefined : 300,
        maxWidth: 700,
        cellRenderer: col.field === "tableIndex" ? undefined : DefaultCellRenderer,
        // tooltipField: col.field,
        cellStyle:
          col.field === "tableIndex"
            ? {
                justifyContent: "center",
                display: "flex",
                alignItems: "center",
                padding: "0 8px",
              }
            : (params: any) => {
                const field = col.field as string;
                if (field && params.data?.errors?.[field]) {
                  const severity = params.data.errors[field][0]?.severity as keyof typeof CELL_COLOR_MAP;
                  return {
                    backgroundColor: severity ? CELL_COLOR_MAP[severity] : "transparent",
                  };
                }
                return { backgroundColor: "transparent" };
              },
      }));

      const indexColumn = columnsWithMaxWidth.find((col) => col.field === "tableIndex");
      const otherColumns = columnsWithMaxWidth.filter((col) => col.field !== "tableIndex");

      if (!indexColumn) return;

      setColumnDefs(shouldShowStatus ? [indexColumn, statusValueColumn, statusMessageColumn, ...otherColumns] : columnsWithMaxWidth);
    }, [newRows, columns, importStatus]);

    useEffect(() => {
      setTimeout(() => {
        if (isVisible && isFirstTime.current) {
          gridRef.current?.autoSizeColumns(
            gridRef.current
              ?.getColumns()
              ?.filter((col) => col.getColId() !== "tableIndex")
              .map((col) => col.getColId()) || []
          );
          isFirstTime.current = false;
        }
      }, 5);
    }, [isVisible]);

    const handleSelectRow = ({ api }: any) => {
      // Skip selection updates during filter changes
      if (isFilterChanging.current) {
        return;
      }

      const selectedRows = api.getSelectedRows();
      if (selectedRows.length === 0) {
        onSelectRows(null);
        return;
      }

      const mappedRows = selectedRows.map((data: any) => ({
        id: data.index,
        index: data.index,
        tableIndex: data.tableIndex || Number(data.index) + 1,
        destination_values: data.destination_values,
        source_values: data.source_values,
        errors: data.errors,
        status: data.status,
        rowIndex: data.index,
      }));

      onSelectRows(mappedRows);
    };

    const navigateToNextCell = (params: any) => {
      const suggestedNextCell = params.nextCellPosition;

      params.api.forEachNode((node: any) => {
        if (node.rowIndex === suggestedNextCell.rowIndex) {
          node.setSelected(true);
        } else {
          node.setSelected(false);
        }
      });

      handleSelectRow({ api: params.api });
      return suggestedNextCell;
    };

    const setDataSource = async () => {
      const dataSource: IDatasource = {
        rowCount: paginatedDataRef.current?.total || undefined,
        getRows: async (params: any) => {
          try {
            const newData = await fetchRows(importId ?? "", filterRef.current, PAGINATION_BLOCK_SIZE, paginatedDataRef.current?.next_offset || 0);
            const paginationInfo = newData?.pagination;
            paginatedDataRef.current = paginationInfo;
            // const nextOffset = paginationInfo?.next_offset || 0;
            const tableData = {
              ...newData,
              rows:
                newData?.rows.map(({ destination_values, index, source_values, errors, status }) => {
                  return {
                    tableIndex: Number(index) + 1,
                    rowIndex: index,
                    index,
                    ...destination_values,
                    destination_values,
                    source_values,
                    errors,
                    status,
                  };
                }) || [],
            };
            const rowsThisPage = tableData.rows;

            let lastRow = -1;
            if (paginationInfo?.total !== undefined && paginationInfo.total <= params.endRow) {
              lastRow = newData?.pagination?.total || 0;
            }
            params.successCallback(rowsThisPage, lastRow);
            setNewRows(rowsThisPage);
            if (importId && importId !== previousPipelineImport.current) {
              previousPipelineImport.current = importId;
            }
          } catch (error) {
            console.log("Error fetching rows", error);
          }
        },
      };
      gridRef.current?.setGridOption?.("datasource", dataSource);
    };

    const onGridReady = useCallback(
      (params: GridReadyEvent<any>) => {
        gridRef.current = params.api as any;
        setDataSource();
      },
      [JSON.stringify(columns)]
    );

    return (
      <Box sx={styles.wrapper}>
        <div className={classes(["grid-wrapper", theme === "dark" ? "ag-theme-alpine-dark" : "ag-theme-alpine"])}>
          <AgGridReact
            rowModelType="infinite"
            cacheOverflowSize={1}
            maxConcurrentDatasourceRequests={1}
            cacheBlockSize={PAGINATION_BLOCK_SIZE}
            maxBlocksInCache={10}
            tooltipShowDelay={500}
            columnDefs={columnDefs}
            rowSelection="multiple"
            onSelectionChanged={handleSelectRow}
            onGridReady={onGridReady}
            suppressDragLeaveHidesColumns
          />
        </div>
      </Box>
    );
  }
);

export default ReviewDataTable;
